import React from 'react';
import { motion } from 'framer-motion';
import { useMeteoCache } from '../hooks/useMeteoCache';
import type { WeatherData } from '../hooks/useMeteoCache';
const EMPTY_DATA: WeatherData = {
  temp: '—',
  dew: '—',
  humidity: '—',
  windSpeed: '—',
  windGust: '—',
  windDir: '—',
  civilDawn: '—',
  civilDusk: '—'
};
export function Meteo() {
  const {
    weather,
    qnh,
    loading,
    weatherError
  } = useMeteoCache();
  const data = weather || EMPTY_DATA;
  const qnhDisplay = qnh || '—';
  const error = weatherError;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    delay: 0.5
  }} className="w-full max-w-7xl mx-auto px-6 mb-12 relative z-20" data-id="element-141">
      {/* Mobile: METEO heading + QNH centered above data */}
      <div className="lg:hidden text-center mb-6" data-id="element-142">
        <h3 className="text-[#38bdf8] font-thin text-2xl tracking-[0.2em] uppercase mb-1" data-id="element-143">
          Meteo
        </h3>
        <p className="text-sm" data-id="element-144">
          <span className="text-[#38bdf8] font-bold" data-id="element-145">QNH</span>
          <span className="text-[#38bdf8]/60 font-thin mx-1" data-id="element-146">LKPR</span>
          <span className={`text-[#38bdf8] font-bold font-mono ${qnhDisplay === '—' ? 'animate-pulse opacity-30' : ''}`} data-id="element-147">
            {qnhDisplay}
          </span>
        </p>
      </div>

      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12" data-id="element-148">
        {/* Desktop: Label with QNH */}
        <div className="hidden lg:block shrink-0" data-id="element-149">
          <div className="border-l-2 border-[#38bdf8] pl-4 flex flex-col gap-1" data-id="element-150">
            <h3 className="text-[#38bdf8] font-thin text-2xl tracking-[0.2em] uppercase" data-id="element-151">
              Meteo
            </h3>
            <p className="text-sm" data-id="element-152">
              <span className="text-[#38bdf8] font-bold" data-id="element-153">QNH</span>
              <span className="text-[#38bdf8]/60 font-thin mx-1" data-id="element-154">LKPR</span>
              <span className={`text-[#38bdf8] font-bold font-mono ${qnhDisplay === '—' ? 'animate-pulse opacity-30' : ''}`} data-id="element-155">
                {qnhDisplay}
              </span>
            </p>
          </div>
        </div>

        {/* Data Grid — centered on mobile */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-8 lg:gap-4" data-id="element-156">
          <WeatherItem label="Teplota" value={data.temp} unit="°C" loading={loading} data-id="element-157" />
          <WeatherItem label="Rosný bod" value={data.dew} unit="°C" loading={loading} data-id="element-158" />
          <WeatherItem label="Vlhkost" value={data.humidity} unit="%" loading={loading} data-id="element-159" />
          <WeatherItem label="Vítr" value={data.windSpeed} unit="kt" loading={loading} data-id="element-160" />
          <WeatherItem label="Nárazy" value={data.windGust} unit="kt" loading={loading} data-id="element-161" />
          <WeatherItem label="Směr" value={data.windDir} unit="°" loading={loading} data-id="element-162" />

          {/* Dawn/Dusk Group */}
          <div className="col-span-2 flex flex-col justify-center items-center lg:items-start gap-1.5 border-t sm:border-t-0 sm:border-l border-white/10 pt-4 sm:pt-0 sm:pl-4 mt-2 sm:mt-0" data-id="element-163">
            <div className="flex items-baseline gap-2 text-xs text-white/40" data-id="element-164">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-165">
                Úsvit
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-166">
                {data.civilDawn}
              </span>
            </div>
            <div className="flex items-baseline gap-2 text-xs text-white/40" data-id="element-167">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-168">
                Soumrak
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-169">
                {data.civilDusk}
              </span>
            </div>
          </div>
        </div>
      </div>

      {error && <p className="text-white/20 text-[10px] text-center mt-4 font-mono" data-id="element-170">
          meteo nedostupné ({error})
        </p>}
    </motion.div>;
}
function WeatherItem({
  label,
  value,
  unit,
  loading
}: {
  label: string;
  value: string;
  unit: string;
  loading?: boolean;
}) {
  return <div className="flex flex-col items-center lg:items-start" data-id="element-171">
      <span className="text-[10px] uppercase tracking-widest text-white/40 mb-1" data-id="element-172">
        {label}
      </span>
      <div className="flex items-baseline" data-id="element-173">
        <span className={`text-3xl font-extralight tracking-tighter ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-174">
          {value}
        </span>
        <span className="ml-1 text-sm text-[#38bdf8]/50 font-light" data-id="element-175">
          {unit}
        </span>
      </div>
    </div>;
}